/**
 * Google Analytics — Consent Mode v2 bridge.
 *
 * The gtag.js loader and `gtag('consent', 'default', { ...denied })` call live
 * in public/index.html so they execute on the very first paint, before any
 * cookies could be written.
 *
 * This module reacts to user decisions made in the cookie banner and forwards
 * them to gtag via `consent: update`:
 *   - analytics  -> analytics_storage
 *   - functional -> functionality_storage + personalization_storage
 *
 * It also wipes `_ga*` cookies when the user revokes analytics consent so the
 * existing client_id is dropped immediately.
 */

import {
  getConsent,
  onConsentChange,
  isCategoryAllowed,
  ConsentCategories,
} from '@/libs/consent'

// NOTE: must push the `arguments` object (array-like), not a real Array.
// gtag.js silently ignores `consent` commands if `dataLayer` contains a true
// Array instead of the `arguments` object produced by the canonical
// `function gtag(){dataLayer.push(arguments);}` wrapper used in index.html.
// `event` commands tolerate both forms, which is why trackEvent appeared to
// work before — but `consent` updates did not, leaving every hit at gcs=G100.
function gtag() {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments)
}

function clearGaCookies() {
  if (typeof document === 'undefined') return
  const host = window.location.hostname
  // Cover apex + any subdomain depth.
  const parts = host.split('.')
  const domains = []
  for (let i = 0; i < parts.length - 1; i += 1) {
    domains.push(`.${parts.slice(i).join('.')}`)
  }
  domains.push(host)

  document.cookie.split(';').forEach(rawCookie => {
    const eq = rawCookie.indexOf('=')
    const name = (eq > -1 ? rawCookie.slice(0, eq) : rawCookie).trim()
    if (!name) return
    if (!/^_ga/.test(name) && name !== '_gid' && name !== '_gat') return
    domains.forEach(domain => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`
    })
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
  })
}

function pushConsentUpdate(consent) {
  const analyticsGranted = !!(consent && consent.analytics)
  const functionalGranted = !!(consent && consent.functional)

  gtag('consent', 'update', {
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
    functionality_storage: functionalGranted ? 'granted' : 'denied',
    personalization_storage: functionalGranted ? 'granted' : 'denied',
  })

  if (!analyticsGranted) clearGaCookies()
}

/**
 * Push a custom event to GA4.
 *
 * Usage:
 *   import { trackEvent } from '@/libs/analytics'
 *   trackEvent('sign_up_click', { location: 'header' })
 *
 * Behaviour under Consent Mode v2:
 *   - Before analytics consent: gtag still receives the event but it is sent
 *     in cookieless mode (no client_id, used by GA4 conversion modeling).
 *   - After analytics consent: full attribution with client_id.
 *
 * `language` (current locale) is attached automatically so we don't have to
 * pass it from every call site.
 */
// eslint-disable-next-line import/prefer-default-export
export function trackEvent(name, params = {}) {
  if (typeof window === 'undefined' || !name) return
  let language
  try {
    language = window.localStorage?.getItem('locale') || undefined
  } catch (e) { /* ignore */ }
  gtag('event', name, { ...(language ? { language } : {}), ...params })
}

// On boot: sync gtag state with whatever decision is already persisted.
if (typeof window !== 'undefined') {
  const stored = getConsent()
  if (stored) {
    pushConsentUpdate(stored)
  } else if (!isCategoryAllowed(ConsentCategories.ANALYTICS)) {
    // No decision yet — defaults from index.html already deny everything.
    // Still clear stale GA cookies left from before this rollout.
    clearGaCookies()
  }

  onConsentChange(consent => {
    pushConsentUpdate(consent)
  })
}
